// Promociones del catálogo del portal a la forma de `@/shared/patterns/Promotions`. Las de crédito
// leen `effectChips` (el texto del paso 03 del wizard); las de venta, su `benefitSummary`.
import type { PortalSalePromotion } from '@/api/services/retail-wizard/contract';
import type {
    CatalogItem,
    PlanLevel,
    ScopeWidePromotion,
    SkuPromotion,
} from '@/domains/sales/wizard/types';
import { effectChips, levelLabel } from '@/domains/sales/wizard/promotionDetail';
import {
    formatPromotionValidity,
    type PromotionField,
    type PromotionSummary,
    type PromotionView,
    type PromotionsTabState,
} from '@/shared/patterns/Promotions';

export const VALIDITY_GAP = 'El catálogo del portal no trae la vigencia de la promoción.';
export const SEGMENT_GAP =
    'Acotada a un segmento de clientes; el catálogo solo trae su id, no su nombre.';
export const UNDETAILED_GAP =
    'El catálogo marca una promoción vigente para este producto, pero no trae su detalle.';
export const SALE_UNAVAILABLE_GAP =
    'Las promociones de venta no se pudieron leer; solo se muestran las de crédito.';
export const SALE_AUDIENCE_GAP = 'El catálogo no trae a qué clientes aplica la promoción de venta.';
export const SKU_SCOPE = 'Este producto';
export const STORE_SCOPE = 'Todo el catálogo de tu tienda';

const LEVEL_ORDER: readonly PlanLevel[] = ['GOLD', 'SILVER', 'BRONZE'];

/** «Todos los niveles» o los niveles que alcanza, de mayor a menor. */
export function audienceText(targetLevels: readonly PlanLevel[]): string {
    if (targetLevels.length === 0) return 'Todos los niveles';
    const levels = LEVEL_ORDER.filter((level) => targetLevels.includes(level)).map(levelLabel);
    return `Nivel ${levels.join(', ')}`;
}

function benefitsOf(promotion: Pick<SkuPromotion, 'effect'>): string[] {
    return effectChips(promotion.effect).map((chip) => chip.label);
}

/** Lo que leen la píldora y la pestaña; `salePromotions: null` = su fuente no respondió. */
type PromotedItem = Pick<CatalogItem, 'hasPromo' | 'promotions'> & {
    salePromotions?: readonly PortalSalePromotion[] | null;
};

function saleBenefits(promotion: PortalSalePromotion): string[] {
    return promotion.benefitSummary === '' ? [] : [promotion.benefitSummary];
}

/** Las promos que nombran al SKU, de crédito y de venta, para la píldora de la tabla. */
export function skuPromotionSummaries(item: PromotedItem): PromotionSummary[] {
    const credit = (item.promotions ?? []).map((promotion) => ({
        id: promotion.promotionId,
        name: promotion.name,
        benefits: benefitsOf(promotion),
        kind: 'CREDIT' as const,
    }));
    const sale = (item.salePromotions ?? []).map((promotion) => ({
        id: promotion.promotionId,
        name: promotion.name,
        benefits: saleBenefits(promotion),
        kind: 'SALE' as const,
    }));
    return [...credit, ...sale];
}

/** El silo marca promo pero no llegó ninguna con detalle: la píldora dice por qué. */
export function undetailedPromotionReason(item: PromotedItem): string | undefined {
    if (!item.hasPromo || skuPromotionSummaries(item).length > 0) return undefined;
    return item.salePromotions === null ? SALE_UNAVAILABLE_GAP : UNDETAILED_GAP;
}

const NO_VALIDITY: PromotionField = { missing: VALIDITY_GAP };

function skuPromotionView(promotion: SkuPromotion): PromotionView {
    return {
        id: promotion.promotionId,
        name: promotion.name,
        benefits: benefitsOf(promotion),
        validity: NO_VALIDITY,
        audience: { text: audienceText(promotion.targetLevels) },
        scope: { text: SKU_SCOPE },
        kind: 'CREDIT',
    };
}

function salePromotionView(promotion: PortalSalePromotion): PromotionView {
    return {
        id: promotion.promotionId,
        name: promotion.name,
        benefits: saleBenefits(promotion),
        validity: { text: formatPromotionValidity(promotion.validFrom, promotion.validUntil) },
        audience: { missing: SALE_AUDIENCE_GAP },
        scope: { text: SKU_SCOPE },
        kind: 'SALE',
    };
}

function scopeWidePromotionView(promotion: ScopeWidePromotion): PromotionView {
    return {
        id: promotion.promotionId,
        name: promotion.name,
        benefits: benefitsOf(promotion),
        validity: NO_VALIDITY,
        audience: {
            text: audienceText(promotion.targetLevels),
            ...(promotion.segmentId.trim() === '' ? {} : { missing: SEGMENT_GAP }),
        },
        scope: { text: STORE_SCOPE },
        kind: 'CREDIT',
    };
}

/**
 * La pestaña: crédito del SKU y de toda la tienda más las de venta. Una fuente caída se dice; si
 * no llegó ninguna con detalle, «sin fuente» en vez de un vacío que diría que no hay.
 */
export function catalogPromotionsState(
    item: PromotedItem,
    scopeWide: readonly ScopeWidePromotion[] | undefined,
    isLoading: boolean,
): PromotionsTabState {
    if (isLoading) return { kind: 'loading' };
    const own = (item.promotions ?? []).map(skuPromotionView);
    const storeWide = (scopeWide ?? []).map(scopeWidePromotionView);
    const sale = (item.salePromotions ?? []).map(salePromotionView);
    const promotions = [...own, ...storeWide, ...sale];
    const saleDown = item.salePromotions === null;
    if (promotions.length === 0 && (item.hasPromo || saleDown)) {
        return { kind: 'noSource', reason: saleDown ? SALE_UNAVAILABLE_GAP : UNDETAILED_GAP };
    }
    return saleDown
        ? { kind: 'ready', promotions, unavailableNote: SALE_UNAVAILABLE_GAP }
        : { kind: 'ready', promotions };
}
